// =============================================================================
// Origin UI — src/pages/requests/RequestDetailPage.tsx
// Full request detail view with status tracker, employee info, selections,
// comments thread, approval history, task summary, and action buttons.
// Phase 3, Task 3.3: Visual status timeline/tracker for requesters.
// Sep 2026: Technology requirements editing for onboarding (Item B6).
// =============================================================================

import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { requestApi } from '@/services/requestApi';
import { api } from '@/services/apiService';
import { StatusBadge, LoadingSpinner, Card, PageHeader, ErrorBanner, ConfirmDialog } from '@/components/common';
import type { RequestDetail } from '@/types/api';
import styles from './requests.module.css';

// ─── Status lifecycle steps (in order) ───
const FULL_LIFECYCLE_STEPS = [
  { key: 'Draft', label: 'Draft' },
  { key: 'Submitted', label: 'Submitted' },
  { key: 'Approved', label: 'Approved' },
  { key: 'TasksInProgress', label: 'Tasks In Progress' },
  { key: 'Completed', label: 'Completed' },
];

const ONBOARDING_LIFECYCLE_STEPS = [
  { key: 'Draft', label: 'Draft' },
  { key: 'Submitted', label: 'Submitted' },
  { key: 'TasksInProgress', label: 'Tasks In Progress' },
  { key: 'Completed', label: 'Completed' },
];

// ─── Editable selection types for tech requirements ───
const HARDWARE_ITEMS = ['Desktop', 'Camera', 'Headset', 'Laptop', 'iPhone', 'iPad'];
const SOFTWARE_ITEMS = ['Eikon', 'Vista', 'Diva', 'X2', 'LiveMTM', 'Vega', 'ICE Web', 'ICE IM'];
const FOLDER_ITEMS = ['Trading Shared', 'Finance Shared', 'HR Shared', 'Operations Shared', 'Technology Shared'];

export function RequestDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [request, setRequest] = useState<RequestDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  // Tech requirements editing state
  const [techEditing, setTechEditing] = useState(false);
  const [techSaving, setTechSaving] = useState(false);
  const [techSelections, setTechSelections] = useState<{ selectionType: string; selectionValue: string; additionalMetadata?: string }[]>([]);

  useEffect(() => {
    if (id) loadRequest(id);
  }, [id]);

  async function loadRequest(requestId: string) {
    try {
      setLoading(true);
      const data = await requestApi.getById(requestId);
      setRequest(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load request');
    } finally {
      setLoading(false);
    }
  }

  // Initialise tech editing state from current selections
  const startTechEditing = useCallback(() => {
    if (!request) return;
    const editableTypes = new Set(['Hardware', 'Software', 'Citrix', 'FolderAccess', 'SoftwareRemarks', 'HardwareRemarks', 'iPhoneType', 'DeskPhoneExt', 'MobileNumber', 'MobilePackage', 'AppBatchDistribution', 'EmailDistributionLists', 'OtherAccess']);
    const editable = request.selections
      .filter(s => editableTypes.has(s.selectionType))
      .map(s => ({ selectionType: s.selectionType, selectionValue: s.selectionValue, additionalMetadata: s.additionalMetadata || undefined }));
    setTechSelections(editable);
    setTechEditing(true);
  }, [request]);

  function techIsSelected(type: string, value: string) {
    return techSelections.some(s => s.selectionType === type && s.selectionValue === value);
  }

  function techToggle(type: string, value: string) {
    setTechSelections(prev => {
      const exists = prev.some(s => s.selectionType === type && s.selectionValue === value);
      if (exists) return prev.filter(s => !(s.selectionType === type && s.selectionValue === value));
      return [...prev, { selectionType: type, selectionValue: value }];
    });
  }

  function techGetRemark(type: string): string {
    return techSelections.find(s => s.selectionType === type)?.selectionValue || '';
  }

  function techSetRemark(type: string, value: string) {
    setTechSelections(prev => {
      const without = prev.filter(s => s.selectionType !== type);
      if (value.trim()) return [...without, { selectionType: type, selectionValue: value.trim() }];
      return without;
    });
  }

  async function saveTechRequirements() {
    if (!id) return;
    try {
      setTechSaving(true);
      setError('');
      await api.put(`/api/requests/${id}/tech-requirements`, { selections: techSelections });
      setTechEditing(false);
      await loadRequest(id);
    } catch (err: any) {
      const body = err.body ? JSON.parse(err.body) : null;
      setError(body?.error || err.message || 'Failed to save technology requirements');
    } finally {
      setTechSaving(false);
    }
  }

  async function handleSubmit() {
    if (!id) return;
    try {
      setSubmitting(true);
      await requestApi.submit(id);
      await loadRequest(id);
    } catch (err: any) {
      const body = err.body ? JSON.parse(err.body) : null;
      setError(body?.error || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleCancel() {
    if (!id) return;
    try {
      setSubmitting(true);
      await requestApi.cancel(id, { reason: cancelReason || undefined });
      setCancelDialogOpen(false);
      await loadRequest(id);
    } catch (err: any) {
      const body = err.body ? JSON.parse(err.body) : null;
      setError(body?.error || 'Failed to cancel');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleAddComment() {
    if (!id || !commentText.trim()) return;
    try {
      setSubmitting(true);
      await requestApi.addComment(id, { commentText: commentText.trim() });
      setCommentText('');
      await loadRequest(id);
    } catch (err: any) {
      setError('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return <LoadingSpinner />;
  if (!request) return <ErrorBanner message="Request not found" />;

  const r = request;
  const requestTypeLabel = r.requestType === 'Transfer' ? 'Department Transfer' : r.requestType;
  const isCancelled = r.status === 'Cancelled';
  const isReturned = r.status === 'Returned';

  const lifecycleSteps = r.requestType === 'Onboarding' ? ONBOARDING_LIFECYCLE_STEPS : FULL_LIFECYCLE_STEPS;
  const currentStepIdx = lifecycleSteps.findIndex(s => s.key === r.status);
  const displayStepIdx = isReturned ? 1 : (currentStepIdx >= 0 ? currentStepIdx : -1);

  const totalTasks = r.tasks.length;
  const completedTasks = r.tasks.filter(t => t.status === 'Completed').length;

  return (
    <div>
      <PageHeader
        title={r.fullName}
        subtitle={`${requestTypeLabel} · ${r.oqtEmployeeId} · ${r.oqtEmailAddress}`}
        action={<StatusBadge status={r.status} />}
      />

      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}

      {/* ── Status Tracker ── */}
      <div className={styles.statusTracker}>
        {isCancelled ? (
          <div className={styles.statusTrackerCancelled}>
            <span className={styles.statusTrackerCancelledIcon}>✕</span>
            <div>
              <div className={styles.statusTrackerCancelledTitle}>Request Cancelled</div>
              <div className={styles.statusTrackerCancelledDate}>
                {r.cancelledAt && new Date(r.cancelledAt).toLocaleString()}
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className={styles.statusTrackerTrack}>
              <div
                className={styles.statusTrackerFill}
                style={{ width: displayStepIdx >= 0 ? `${(displayStepIdx / (lifecycleSteps.length - 1)) * 100}%` : '0%' }}
              />
            </div>
            <div className={styles.statusTrackerSteps}>
              {lifecycleSteps.map((step, idx) => {
                const isComplete = idx < displayStepIdx;
                const isCurrent = idx === displayStepIdx;
                const isReturnedStep = isReturned && idx === 1;

                let stepClass = styles.statusTrackerStep;
                if (isComplete) stepClass += ` ${styles.statusTrackerStepComplete}`;
                if (isCurrent && !isReturned) stepClass += ` ${styles.statusTrackerStepCurrent}`;
                if (isReturnedStep) stepClass += ` ${styles.statusTrackerStepReturned}`;

                return (
                  <div key={step.key} className={stepClass}>
                    <span className={styles.statusTrackerDot}>
                      {isComplete ? '✓' : isReturnedStep ? '↩' : idx + 1}
                    </span>
                    <span className={styles.statusTrackerLabel}>
                      {isReturnedStep ? 'Returned' : step.label}
                    </span>
                    {step.key === 'TasksInProgress' && totalTasks > 0 && (idx <= displayStepIdx) && (
                      <span className={styles.statusTrackerMeta}>
                        {completedTasks}/{totalTasks} tasks done
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Action bar */}
      <div className={styles.actionBar}>
        {r.actions.canEdit && (
          <Link to={`/requests/${r.requestId}/edit`} className={styles.btnOutline}>
            Edit Request
          </Link>
        )}
        {r.actions.canSubmit && r.status === 'Draft' && (
          <button className={styles.btnPrimary} onClick={handleSubmit} disabled={submitting}>
            Submit for Approval
          </button>
        )}
        {r.actions.canCancel && (
          <button className={styles.btnDanger} onClick={() => setCancelDialogOpen(true)} disabled={submitting}>
            Cancel Request
          </button>
        )}
      </div>

      <div className={styles.detailLayout}>
        {/* Left column: details */}
        <div className={styles.detailMain}>
          {/* Returned banner */}
          {r.status === 'Returned' && (
            <div className={styles.returnedBanner}>
              <strong>Returned for changes</strong>
              {r.comments
                .filter(c => c.commentText.startsWith('[Returned'))
                .slice(-1)
                .map(c => (
                  <p key={c.commentId}>
                    <em>{c.authorDisplayName}:</em>{' '}
                    {c.commentText.replace('[Returned for changes] ', '')}
                  </p>
                ))
              }
              <Link to={`/requests/${r.requestId}/edit`} className={styles.btnPrimary} style={{ marginTop: 12, display: 'inline-block' }}>
                Edit & Resubmit
              </Link>
            </div>
          )}

          {/* Employee Details */}
          <Card className={styles.formSection}>
            <h3 className={styles.sectionTitle}>Employee Details</h3>
            <div className={styles.detailGrid}>
              <DetailField label="Employee ID" value={r.oqtEmployeeId} />
              <DetailField label="Full Name" value={r.fullName} />
              <DetailField label="Email" value={r.oqtEmailAddress} />
              <DetailField label="Designation" value={r.designation} />
              <DetailField label="Department" value={r.department} />
              <DetailField label="Office" value={r.officeLocation} />
              <DetailField label="Line Manager" value={r.lineManagerEmail} />
              <DetailField label="Effective Date" value={new Date(r.effectiveDate).toLocaleDateString()} />
              {r.preferredInitials && <DetailField label="Initials" value={r.preferredInitials} />}
            </div>
          </Card>

          {/* ── Technology Requirements Indicator + Editing ── */}
          {r.requestType === 'Onboarding' && r.status === 'TasksInProgress' && (
            <Card className={styles.formSection}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: techEditing ? 16 : 0 }}>
                <h3 className={styles.sectionTitle} style={{ margin: 0 }}>Technology Requirements</h3>
                {r.actions.canEditTechRequirements && !techEditing && (
                  <button className={styles.btnOutline} onClick={startTechEditing} style={{ fontSize: 13, padding: '6px 14px' }}>
                    Edit Requirements
                  </button>
                )}
              </div>

              {/* Confirmed / Awaiting indicator */}
              {!techEditing && (
                <div style={{
                  marginTop: 8,
                  padding: '10px 14px',
                  borderRadius: 6,
                  fontSize: 13,
                  backgroundColor: r.techRequirementsConfirmedAt ? '#F0FDF4' : '#FFFBEB',
                  border: r.techRequirementsConfirmedAt ? '1px solid #BBF7D0' : '1px solid #FDE68A',
                  color: r.techRequirementsConfirmedAt ? '#166534' : '#92400E',
                }}>
                  {r.techRequirementsConfirmedAt
                    ? `✓ Technology requirements confirmed by ${r.techRequirementsConfirmedBy} on ${new Date(r.techRequirementsConfirmedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} at ${new Date(r.techRequirementsConfirmedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
                    : '⏳ Awaiting manager input — using department defaults'}
                </div>
              )}

              {/* Inline editing panel */}
              {techEditing && (
                <div>
                  {/* Hardware */}
                  <div style={{ marginBottom: 16 }}>
                    <h4 style={{ fontSize: 14, fontWeight: 600, color: '#0A2240', marginBottom: 8 }}>Hardware</h4>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {HARDWARE_ITEMS.map(item => (
                        <label key={item} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                          <input type="checkbox" checked={techIsSelected('Hardware', item)} onChange={() => techToggle('Hardware', item)} />
                          {item}
                        </label>
                      ))}
                    </div>
                    <div style={{ marginTop: 8 }}>
                      <input
                        style={{ width: '100%', padding: '6px 10px', fontSize: 13, border: '1px solid #D1D5DB', borderRadius: 4 }}
                        placeholder="Hardware remarks..."
                        value={techGetRemark('HardwareRemarks')}
                        onChange={e => techSetRemark('HardwareRemarks', e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Software + Citrix */}
                  <div style={{ marginBottom: 16 }}>
                    <h4 style={{ fontSize: 14, fontWeight: 600, color: '#0A2240', marginBottom: 8 }}>Software &amp; Applications</h4>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {SOFTWARE_ITEMS.map(item => (
                        <label key={item} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                          <input type="checkbox" checked={techIsSelected('Software', item)} onChange={() => techToggle('Software', item)} />
                          {item}
                        </label>
                      ))}
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>
                        <input type="checkbox" checked={techIsSelected('Citrix', 'Citrix Access')} onChange={() => techToggle('Citrix', 'Citrix Access')} />
                        Citrix Access
                      </label>
                    </div>
                    <div style={{ marginTop: 8 }}>
                      <input
                        style={{ width: '100%', padding: '6px 10px', fontSize: 13, border: '1px solid #D1D5DB', borderRadius: 4 }}
                        placeholder="Software remarks..."
                        value={techGetRemark('SoftwareRemarks')}
                        onChange={e => techSetRemark('SoftwareRemarks', e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Folder Access */}
                  <div style={{ marginBottom: 16 }}>
                    <h4 style={{ fontSize: 14, fontWeight: 600, color: '#0A2240', marginBottom: 8 }}>Folder Access</h4>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {FOLDER_ITEMS.map(item => (
                        <label key={item} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                          <input type="checkbox" checked={techIsSelected('FolderAccess', item)} onChange={() => techToggle('FolderAccess', item)} />
                          {item}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Free-text fields */}
                  <div style={{ marginBottom: 16 }}>
                    <h4 style={{ fontSize: 14, fontWeight: 600, color: '#0A2240', marginBottom: 8 }}>Additional Access</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <input
                        style={{ width: '100%', padding: '6px 10px', fontSize: 13, border: '1px solid #D1D5DB', borderRadius: 4 }}
                        placeholder="App / Batch Distribution..."
                        value={techGetRemark('AppBatchDistribution')}
                        onChange={e => techSetRemark('AppBatchDistribution', e.target.value)}
                      />
                      <input
                        style={{ width: '100%', padding: '6px 10px', fontSize: 13, border: '1px solid #D1D5DB', borderRadius: 4 }}
                        placeholder="Email / Distribution Lists..."
                        value={techGetRemark('EmailDistributionLists')}
                        onChange={e => techSetRemark('EmailDistributionLists', e.target.value)}
                      />
                      <input
                        style={{ width: '100%', padding: '6px 10px', fontSize: 13, border: '1px solid #D1D5DB', borderRadius: 4 }}
                        placeholder="Other access..."
                        value={techGetRemark('OtherAccess')}
                        onChange={e => techSetRemark('OtherAccess', e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Save / Cancel buttons */}
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', borderTop: '1px solid #E5E7EB', paddingTop: 12 }}>
                    <button className={styles.btnGhost} onClick={() => setTechEditing(false)} disabled={techSaving} type="button">
                      Cancel
                    </button>
                    <button className={styles.btnPrimary} onClick={saveTechRequirements} disabled={techSaving} type="button">
                      {techSaving ? 'Saving...' : 'Save & Confirm Requirements'}
                    </button>
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* Selections — grouped by category for readability */}
          {r.selections.length > 0 && (() => {
            const byType = (type: string) => r.selections.filter(s => s.selectionType === type).map(s => s.selectionValue);
            const getRemark = (type: string) => r.selections.find(s => s.selectionType === type)?.selectionValue;
            const hwItems = byType('Hardware');
            const swItems = byType('Software');
            const hasCitrix = r.selections.some(s => s.selectionType === 'Citrix');
            const folderItems = byType('FolderAccess');
            const techItems = byType('Technology');
            const swRemarks = getRemark('SoftwareRemarks');
            const hwRemarks = getRemark('HardwareRemarks');
            const iPhoneType = getRemark('iPhoneType');
            const deskPhoneExt = getRemark('DeskPhoneExt');
            const mobileNumber = getRemark('MobileNumber');
            const mobilePackage = getRemark('MobilePackage');
            const appBatch = getRemark('AppBatchDistribution');
            const emailDist = getRemark('EmailDistributionLists');
            const otherAccess = getRemark('OtherAccess');

            const tagStyle = { display: 'inline-block' as const, padding: '3px 10px', fontSize: 12, fontWeight: 500, borderRadius: 4, backgroundColor: '#F3F4F6', color: '#374151', marginRight: 6, marginBottom: 4 };
            const sectionStyle = { marginBottom: 14 };
            const headingStyle = { fontSize: 13, fontWeight: 700 as const, color: '#0A2240', marginBottom: 6, textTransform: 'uppercase' as const, letterSpacing: '0.5px' };
            const noteStyle = { fontSize: 13, color: '#6B7280', marginTop: 4 };

            return (
              <Card className={styles.formSection}>
                <h3 className={styles.sectionTitle}>Request Requirements</h3>

                {/* Hardware */}
                {(hwItems.length > 0 || iPhoneType || deskPhoneExt || mobileNumber || hwRemarks) && (
                  <div style={sectionStyle}>
                    <div style={headingStyle}>Hardware</div>
                    <div>{hwItems.map(item => <span key={item} style={tagStyle}>{item}</span>)}</div>
                    {hasCitrix && <span style={{ ...tagStyle, backgroundColor: '#EEF2FF', color: '#4338CA' }}>Citrix Access</span>}
                    {iPhoneType && <p style={noteStyle}>iPhone Type: {iPhoneType}</p>}
                    {deskPhoneExt && <p style={noteStyle}>Desk Phone Ext: {deskPhoneExt}</p>}
                    {mobileNumber && <p style={noteStyle}>Mobile: {mobileNumber}</p>}
                    {mobilePackage && <p style={noteStyle}>Package: {mobilePackage}</p>}
                    {hwRemarks && <p style={noteStyle}>Remarks: {hwRemarks}</p>}
                  </div>
                )}

                {/* Software */}
                {(swItems.length > 0 || swRemarks) && (
                  <div style={sectionStyle}>
                    <div style={headingStyle}>Software &amp; Applications</div>
                    <div>{swItems.map(item => <span key={item} style={tagStyle}>{item}</span>)}</div>
                    {!hwItems.length && hasCitrix && <span style={{ ...tagStyle, backgroundColor: '#EEF2FF', color: '#4338CA' }}>Citrix Access</span>}
                    {swRemarks && <p style={noteStyle}>Remarks: {swRemarks}</p>}
                  </div>
                )}

                {/* Folder Access */}
                {folderItems.length > 0 && (
                  <div style={sectionStyle}>
                    <div style={headingStyle}>Folder Access</div>
                    <div>{folderItems.map(item => <span key={item} style={tagStyle}>{item}</span>)}</div>
                  </div>
                )}

                {/* Distribution / Additional Access */}
                {(appBatch || emailDist || otherAccess) && (
                  <div style={sectionStyle}>
                    <div style={headingStyle}>Distribution &amp; Additional Access</div>
                    {appBatch && <p style={noteStyle}><strong>App/Batch Distribution:</strong> {appBatch}</p>}
                    {emailDist && <p style={noteStyle}><strong>Email/Distribution Lists:</strong> {emailDist}</p>}
                    {otherAccess && <p style={noteStyle}><strong>Other Access:</strong> {otherAccess}</p>}
                  </div>
                )}

                {/* Technology items */}
                {techItems.length > 0 && (
                  <div style={sectionStyle}>
                    <div style={headingStyle}>Standard Technology</div>
                    <div>{techItems.map(item => <span key={item} style={{ ...tagStyle, backgroundColor: '#F0FDFA', color: '#0F766E' }}>{item}</span>)}</div>
                  </div>
                )}
              </Card>
            );
          })()}

          {/* Tasks — grouped by stakeholder role */}
          {r.tasks.length > 0 && (() => {
            const getRole = (t: { taskType: string; description?: string; assignedToDisplayName: string | null }) => {
              const desc = (t.description || '').toLowerCase();
              if (desc.includes('assigned to hradmin') || desc.includes('assigned to hr admin')) return 'HR';
              if (desc.includes('assigned to finance')) return 'Finance';
              if (desc.includes('assigned to manager') || desc.includes('assigned to line manager')) return 'Manager';
              if (desc.includes('assigned to newhire') || desc.includes('assigned to new hire')) return 'New Hire';
              return 'IT';
            };
            const groups: Record<string, typeof r.tasks> = {};
            const groupOrder = ['IT', 'HR', 'Finance', 'Manager', 'New Hire'];
            const groupIcons: Record<string, string> = { IT: '💻', HR: '👤', Finance: '💰', Manager: '👔', 'New Hire': '🆕' };
            for (const t of r.tasks) {
              const role = getRole(t);
              if (!groups[role]) groups[role] = [];
              groups[role].push(t);
            }
            const activeGroups = groupOrder.filter(g => groups[g]?.length > 0);
            return (
              <Card className={styles.formSection}>
                <h3 className={styles.sectionTitle}>Tasks ({r.tasks.length})</h3>
                {activeGroups.map(group => (
                  <div key={group} style={{ marginBottom: activeGroups.length > 1 ? '16px' : '0' }}>
                    {activeGroups.length > 1 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', paddingBottom: '6px', borderBottom: '1px solid #E5E7EB' }}>
                        <span style={{ fontSize: '14px' }}>{groupIcons[group]}</span>
                        <span style={{ fontSize: '13px', fontWeight: 600, color: '#0A2240', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          {group} Tasks ({groups[group].length})
                        </span>
                        <span style={{ fontSize: '12px', color: '#6B7280', marginLeft: 'auto' }}>
                          {groups[group].filter(t => t.status === 'Completed').length}/{groups[group].length} done
                        </span>
                      </div>
                    )}
                    <div className={styles.tasksList}>
                      {groups[group].map(t => (
                        <div key={t.taskId} className={styles.taskRow}>
                          <span className={styles.taskType}>{t.taskType}</span>
                          <StatusBadge status={t.status} />
                          {t.assignedToDisplayName && (
                            <span className={styles.muted}>{t.assignedToDisplayName}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </Card>
            );
          })()}

          {/* Comments */}
          <Card className={styles.formSection}>
            <h3 className={styles.sectionTitle}>Comments ({r.comments.length})</h3>
            {r.comments.length > 0 ? (
              <div className={styles.commentsList}>
                {r.comments.map(c => (
                  <div key={c.commentId} className={styles.commentItem}>
                    <div className={styles.commentHeader}>
                      <span className={styles.commentAuthor}>{c.authorDisplayName}</span>
                      <span className={styles.commentRole}>{c.authorRole}</span>
                      <span className={styles.commentDate}>
                        {new Date(c.createdAt).toLocaleString()}
                      </span>
                    </div>
                    <p className={styles.commentText}>{c.commentText}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className={styles.muted}>No comments yet.</p>
            )}

            {r.actions.canAddComment && (
              <div className={styles.addComment}>
                <textarea
                  className={styles.textarea}
                  placeholder="Add a comment..."
                  value={commentText}
                  onChange={e => setCommentText(e.target.value)}
                  rows={3}
                />
                <button
                  className={styles.btnPrimary}
                  onClick={handleAddComment}
                  disabled={!commentText.trim() || submitting}
                >
                  Add Comment
                </button>
              </div>
            )}
          </Card>
        </div>

        {/* Right column: timeline */}
        <div className={styles.detailSidebar}>
          <Card>
            <h4 className={styles.sidebarTitle}>Timeline</h4>
            <div className={styles.timeline}>
              <TimelineItem label="Created" date={r.createdAt} by={r.createdByDisplayName} />
              {r.submittedAt && <TimelineItem label="Submitted" date={r.submittedAt} />}
              {r.approvals.map(a => (
                <TimelineItem
                  key={a.approvalId}
                  label={a.decision === 'Approved' ? 'Approved' : 'Returned'}
                  date={a.decisionTimestamp}
                  by={a.approverDisplayName}
                />
              ))}
              {r.approvedAt && <TimelineItem label="Approved" date={r.approvedAt} />}
              {r.completedAt && <TimelineItem label="Completed" date={r.completedAt} />}
              {r.cancelledAt && <TimelineItem label="Cancelled" date={r.cancelledAt} />}
            </div>
          </Card>

          <Card>
            <h4 className={styles.sidebarTitle}>Details</h4>
            <div className={styles.sidebarMeta}>
              <div><span>Request ID</span><span className={styles.muted}>{r.requestId.slice(0, 8)}...</span></div>
              <div><span>Created by</span><span>{r.createdByDisplayName}</span></div>
              <div><span>Request Type</span><span>{requestTypeLabel}</span></div>
            </div>
          </Card>

          {/* Task progress ring */}
          {totalTasks > 0 && (
            <Card>
              <h4 className={styles.sidebarTitle}>Task Progress</h4>
              <div className={styles.progressRingWrap}>
                <svg width="100" height="100" viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="42" fill="none" stroke="var(--gray-200)" strokeWidth="8" />
                  <circle
                    cx="50" cy="50" r="42" fill="none"
                    stroke={completedTasks === totalTasks ? 'var(--green)' : 'var(--turquoise)'}
                    strokeWidth="8"
                    strokeDasharray={2 * Math.PI * 42}
                    strokeDashoffset={2 * Math.PI * 42 * (1 - completedTasks / totalTasks)}
                    strokeLinecap="round"
                    style={{ transform: 'rotate(-90deg)', transformOrigin: '50px 50px', transition: 'stroke-dashoffset 0.5s ease' }}
                  />
                </svg>
                <div className={styles.progressRingLabel}>{completedTasks}/{totalTasks}</div>
              </div>
              <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--gray-500)', marginTop: 4 }}>
                {completedTasks === totalTasks ? 'All tasks complete' : 'tasks completed'}
              </p>
            </Card>
          )}
        </div>
      </div>

      {/* Cancel dialog */}
      <ConfirmDialog
        open={cancelDialogOpen}
        title="Cancel Request"
        message="Are you sure you want to cancel this request? This action is permanent and cannot be undone."
        confirmLabel="Cancel Request"
        confirmVariant="danger"
        onConfirm={handleCancel}
        onCancel={() => setCancelDialogOpen(false)}
      >
        <div style={{ marginBottom: 16 }}>
          <label className={styles.label}>Reason (optional)</label>
          <textarea
            className={styles.textarea}
            value={cancelReason}
            onChange={e => setCancelReason(e.target.value)}
            placeholder="Why is this request being cancelled?"
            rows={3}
          />
        </div>
      </ConfirmDialog>
    </div>
  );
}

// ─── Sub-components ───

function DetailField({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.detailField}>
      <span className={styles.detailLabel}>{label}</span>
      <span className={styles.detailValue}>{value}</span>
    </div>
  );
}

function TimelineItem({ label, date, by }: { label: string; date: string; by?: string }) {
  return (
    <div className={styles.timelineItem}>
      <div className={styles.timelineDot} />
      <div>
        <div className={styles.timelineLabel}>{label}</div>
        <div className={styles.timelineDate}>
          {new Date(date).toLocaleString()}
          {by && <span> · {by}</span>}
        </div>
      </div>
    </div>
  );
}
